import * as React from 'react';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import './HTMLEditorQuill.css';
import './VariableBlot';
import './InlineImageBlot';
import { HTML_EDITOR_QUILL_FORMATS } from './HTMLEditorConst';
import { AppUser } from 'Src/views/App/App';
import { uploadImage } from 'Src/helpers/service/image';

interface Props {
  user?: AppUser;
  imageUploader?: (image: File) => Promise<any>;
  onEmailBodyChange: (value: string) => void;
  value: string;
  isEnabled: boolean;
  isToolbarVisible?: boolean;
  placeholder?: string;
}

export interface HTMLEditorQuillHandle {
  insertVariable: (variable: string) => void;
  focus: () => void;
  insertHtml: (html: string) => void;
}

const EMOJIS = [
  '😀',
  '😊',
  '😂',
  '😍',
  '🤩',
  '👍',
  '👏',
  '🙌',
  '🎉',
  '✅',
  '⚽',
  '🏆',
  '📅',
  '📍',
  '📣',
  '❤️',
  '⭐',
  '🔥',
  '💪',
  '🙏',
  '👋',
  '🙂',
  '😢',
  '⚠️'
];

const INLINE_IMAGE_MAX_SIZE = 5 * 1024 * 1024;
const INLINE_IMAGE_MAX_SOURCE_SIZE = 25 * 1024 * 1024;
const INLINE_IMAGE_MAX_DIMENSION = 1920;
const INLINE_IMAGE_QUALITY = 0.85;

const IMAGE_SIZE_OPTIONS = [
  { label: 'Small', width: '240px' },
  { label: 'Medium', width: '480px' },
  { label: 'Large', width: '720px' },
  { label: 'Original', width: '' }
];

interface SelectedImage {
  element: HTMLImageElement;
  index: number;
  imageTop: number;
  imageLeft: number;
  imageWidth: number;
  imageHeight: number;
  top: number;
  left: number;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to read image.'));
    };
    image.src = objectUrl;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Unable to resize image.'));
        }
      },
      type,
      quality
    );
  });
}

async function resizeInlineImage(file: File): Promise<File> {
  const resizableTypes = ['image/jpeg', 'image/png', 'image/webp'];

  if (resizableTypes.indexOf(file.type) === -1) {
    return file;
  }

  const image = await loadImage(file);
  const largestDimension = Math.max(image.naturalWidth, image.naturalHeight);

  if (largestDimension <= INLINE_IMAGE_MAX_DIMENSION) {
    return file;
  }

  const scale = INLINE_IMAGE_MAX_DIMENSION / largestDimension;
  const width = Math.max(Math.round(image.naturalWidth * scale), 1);
  const height = Math.max(Math.round(image.naturalHeight * scale), 1);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Unable to resize image.');
  }

  context.drawImage(image, 0, 0, width, height);

  const outputType = file.type === 'image/png' ? 'image/png' : file.type === 'image/webp' ? 'image/webp' : 'image/jpeg';
  const blob = await canvasToBlob(canvas, outputType, outputType === 'image/png' ? undefined : INLINE_IMAGE_QUALITY);
  const extension = outputType === 'image/png' ? 'png' : outputType === 'image/webp' ? 'webp' : 'jpg';
  const baseName = file.name.replace(/\.[^.]+$/, '') || 'inline-image';

  return new File([blob], `${baseName}.${extension}`, {
    type: outputType,
    lastModified: Date.now()
  });
}

export const HTMLEditorQuill = forwardRef<HTMLEditorQuillHandle, Props>((props, ref) => {
  const { user, imageUploader, onEmailBodyChange, value, isEnabled, isToolbarVisible = true, placeholder } = props;
  const quillRef = useRef<ReactQuill>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const lastSelectionIndexRef = useRef<number>(0);
  const toolbarIdRef = useRef(
    `gm-editor-toolbar-${Math.random()
      .toString(36)
      .slice(2)}`
  );
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState<boolean>(false);
  const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
  const [imageError, setImageError] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(null);

  const getInsertionIndex = (): number => {
    const quill = quillRef.current?.getEditor();
    const selection = quill?.getSelection();
    return selection
      ? selection.index
      : Math.min(lastSelectionIndexRef.current, Math.max((quill?.getLength() || 1) - 1, 0));
  };

  const insertEmoji = (emoji: string) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const index = getInsertionIndex();
    quill.insertText(index, emoji, 'user');
    quill.setSelection(index + emoji.length, 0, 'silent');
    lastSelectionIndexRef.current = index + emoji.length;
    setIsEmojiPickerOpen(false);
    quill.focus();
  };

  const onImageSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file || (!user && !imageUploader)) return;

    if (!file.type.startsWith('image/')) {
      setImageError('Please select an image file.');
      return;
    }

    if (file.size > INLINE_IMAGE_MAX_SOURCE_SIZE) {
      setImageError('Inline images must be 25 MB or smaller before optimisation.');
      return;
    }

    const insertionIndex = getInsertionIndex();
    setImageError('');
    setIsUploadingImage(true);

    try {
      const resizedFile = await resizeInlineImage(file);

      if (resizedFile.size > INLINE_IMAGE_MAX_SIZE) {
        throw new Error('IMAGE_TOO_LARGE');
      }

      const response = imageUploader
        ? await imageUploader(resizedFile)
        : await uploadImage(user as AppUser, resizedFile);
      const imageUrl =
        response.link ||
        response.picUrl ||
        response.url ||
        (response.key ? `${window.apiImg}/images/${response.key}` : '');

      if (imageUrl === '') {
        throw new Error('Image upload returned no URL.');
      }

      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      quill.insertEmbed(insertionIndex, 'image', imageUrl, 'user');
      quill.formatText(insertionIndex, 1, 'width', '720px', 'user');
      quill.insertText(insertionIndex + 1, '\n', 'user');
      quill.setSelection(insertionIndex + 2, 0, 'silent');
      lastSelectionIndexRef.current = insertionIndex + 2;
      quill.focus();
    } catch (error) {
      setImageError(
        error instanceof Error && error.message === 'IMAGE_TOO_LARGE'
          ? 'The optimised image is still larger than 5 MB. Please choose a smaller image.'
          : 'Unable to upload the image. Please try again.'
      );
    } finally {
      setIsUploadingImage(false);
    }
  };

  const modules = useMemo(
    () => ({
      toolbar: isToolbarVisible
        ? {
            container: `#${toolbarIdRef.current}`,
            handlers: {
              undo: () => quillRef.current?.getEditor()?.history.undo(),
              redo: () => quillRef.current?.getEditor()?.history.redo(),
              emoji: () => isEnabled && setIsEmojiPickerOpen(current => !current),
              image: () => isEnabled && imageInputRef.current?.click()
            }
          }
        : false,
      history: {
        delay: 700,
        maxStack: 100,
        userOnly: true
      }
    }),
    [isEnabled, isToolbarVisible]
  );

  useImperativeHandle(ref, () => ({
    insertVariable: (variable: string) => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      quill.focus();
      const range = quill.getSelection() || { index: quill.getLength() - 1, length: 0 };

      if (range.length > 0) {
        quill.deleteText(range.index, range.length, 'user');
      }

      quill.insertEmbed(range.index, 'variable-chip', variable, 'user');

      quill.insertText(range.index + 1, '\u200B', 'user');
      quill.insertText(range.index + 2, ' ', 'user');
      quill.setSelection(range.index + 1, 2, 'user');
      quill.removeFormat(range.index + 1, 2, 'user');
      quill.setSelection(range.index + 3, 0, 'user');

      ['bold', 'italic', 'underline', 'strike', 'color', 'background', 'font', 'size'].forEach(fmt => {
        quill.format(fmt, false, 'user');
      });
    },
    focus: () => {
      quillRef.current?.getEditor()?.focus();
    },
    insertHtml: (html: string) => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;
      quill.focus();
      const range = quill.getSelection() || { index: quill.getLength() - 1, length: 0 };
      quill.clipboard.dangerouslyPasteHTML(range.index, html, 'user');
      quill.setSelection(quill.getLength(), 0, 'silent');
    }
  }));

  const editorClassName = isEnabled ? 'quillEditor-root' : 'quillEditor-root mDisabled';

  const selectImage = (image: HTMLImageElement) => {
    const quill = quillRef.current?.getEditor();
    const container = editorContainerRef.current;

    if (!quill || !container) return;

    const blot = (Quill as any).find(image);
    if (!blot) return;

    const imageRect = image.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const panelWidth = 360;
    const left = Math.max(8, Math.min(imageRect.left - containerRect.left, container.clientWidth - panelWidth - 8));

    setSelectedImage({
      element: image,
      index: quill.getIndex(blot),
      imageTop: imageRect.top - containerRect.top,
      imageLeft: imageRect.left - containerRect.left,
      imageWidth: imageRect.width,
      imageHeight: imageRect.height,
      top: imageRect.bottom - containerRect.top + 6,
      left
    });
  };

  const onEditorContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const removeVariable = target.closest('[data-variable-remove]');
    if (removeVariable && isEnabled) {
      event.preventDefault();
      event.stopPropagation();
      const chip = removeVariable.closest('.ql-variable-chip');
      const quill = quillRef.current?.getEditor();
      const blot = chip ? Quill.find(chip) : null;
      if (quill && blot) {
        const index = quill.getIndex(blot);
        quill.deleteText(index, 1, 'user');
        quill.setSelection(index, 0, 'silent');
        quill.focus();
      }
      return;
    }

    if (target.tagName === 'IMG' && target.closest('.ql-editor')) {
      selectImage(target as HTMLImageElement);
      return;
    }

    if (!target.closest('.gmQuillImageControls')) {
      setSelectedImage(null);
    }
  };

  const setSelectedImageWidth = (width: string) => {
    const quill = quillRef.current?.getEditor();
    if (!quill || !selectedImage) return;

    quill.formatText(selectedImage.index, 1, 'width', width || false, 'user');
    selectImage(selectedImage.element);
  };

  const deleteSelectedImage = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill || !selectedImage) return;

    quill.deleteText(selectedImage.index, 1, 'user');
    setSelectedImage(null);
    quill.focus();
  };

  const onEditorChange = (html: string) => {
    if (selectedImage && !document.body.contains(selectedImage.element)) {
      setSelectedImage(null);
    }

    onEmailBodyChange(html);
  };

  return (
    <div ref={editorContainerRef} className={editorClassName} onClick={onEditorContainerClick}>
      {isToolbarVisible && (
        <div id={toolbarIdRef.current} className={`gmQuillToolbar ${isEnabled ? '' : 'gmQuillToolbar--disabled'}`}>
          <span className="ql-formats">
            <select className="ql-header" defaultValue="">
              <option value="1">Heading 1</option>
              <option value="2">Heading 2</option>
              <option value="3">Heading 3</option>
              <option value="">Normal</option>
            </select>
            <select className="ql-size" defaultValue="">
              <option value="small">Small</option>
              <option value="">Normal</option>
              <option value="large">Large</option>
              <option value="huge">Huge</option>
            </select>
          </span>
          <span className="ql-formats">
            <button type="button" className="ql-bold" title="Bold" />
            <button type="button" className="ql-italic" title="Italic" />
            <button type="button" className="ql-underline" title="Underline" />
            <button type="button" className="ql-strike" title="Strikethrough" />
            <button type="button" className="ql-blockquote" title="Quote" />
          </span>
          <span className="ql-formats">
            <select className="ql-color" title="Text colour" />
            <select className="ql-background" title="Background colour" />
          </span>
          <span className="ql-formats">
            <button type="button" className="ql-list" value="ordered" title="Numbered list" />
            <button type="button" className="ql-list" value="bullet" title="Bullet list" />
            <button type="button" className="ql-indent" value="-1" title="Decrease indent" />
            <button type="button" className="ql-indent" value="+1" title="Increase indent" />
            <select className="ql-align" title="Alignment" />
          </span>
          <span className="ql-formats">
            <button type="button" className="ql-link" title="Link" />
            <button
              type="button"
              className="ql-image"
              title="Upload inline image"
              disabled={(!user && !imageUploader) || isUploadingImage}
            />
            <button type="button" className="ql-emoji gmQuillToolbar__textButton" title="Insert emoji">
              😊
            </button>
          </span>
          <span className="ql-formats">
            <button type="button" className="ql-undo gmQuillToolbar__textButton" title="Undo">
              ↶
            </button>
            <button type="button" className="ql-redo gmQuillToolbar__textButton" title="Redo">
              ↷
            </button>
            <button type="button" className="ql-clean" title="Clear formatting" />
          </span>
        </div>
      )}
      {isToolbarVisible && isEmojiPickerOpen && (
        <div className="gmQuillEmojiPicker" role="dialog" aria-label="Choose an emoji">
          {EMOJIS.map(emoji => (
            <button key={emoji} type="button" onClick={() => insertEmoji(emoji)} title={`Insert ${emoji}`}>
              {emoji}
            </button>
          ))}
        </div>
      )}
      <input
        ref={imageInputRef}
        className="gmQuillImageInput"
        type="file"
        accept="image/*"
        onChange={onImageSelected}
      />
      {isUploadingImage && <div className="gmQuillStatus">Uploading image…</div>}
      {imageError !== '' && <div className="gmQuillStatus gmQuillStatus--error">{imageError}</div>}
      {selectedImage && isEnabled && (
        <>
          <div
            className="gmQuillImageSelection"
            style={{
              top: selectedImage.imageTop,
              left: selectedImage.imageLeft,
              width: selectedImage.imageWidth,
              height: selectedImage.imageHeight
            }}
          />
          <div
            className="gmQuillImageControls"
            style={{ top: selectedImage.top, left: selectedImage.left }}
            onClick={event => event.stopPropagation()}
          >
            {IMAGE_SIZE_OPTIONS.map(option => {
              const currentWidth =
                selectedImage.element.style.width || selectedImage.element.getAttribute('width') || '';
              const isActive = option.width === currentWidth;

              return (
                <button
                  key={option.label}
                  type="button"
                  className={isActive ? 'is-active' : ''}
                  onClick={() => setSelectedImageWidth(option.width)}
                >
                  {option.label}
                </button>
              );
            })}
            <span className="gmQuillImageControls__separator" />
            <button type="button" className="gmQuillImageControls__delete" onClick={deleteSelectedImage}>
              Delete
            </button>
          </div>
        </>
      )}
      <ReactQuill
        ref={quillRef}
        onChange={onEditorChange}
        value={value}
        modules={modules}
        formats={HTML_EDITOR_QUILL_FORMATS}
        readOnly={!isEnabled}
        placeholder={placeholder || 'Write your email here...'}
        useSemanticHTML={false}
        onChangeSelection={range => {
          if (range) {
            lastSelectionIndexRef.current = range.index;
          }
        }}
      />
    </div>
  );
});

HTMLEditorQuill.displayName = 'HTMLEditorQuill';

export function stripVariableChips(html: string): string {
  if (typeof html !== 'string' || html === '') return html;

  if (typeof document === 'undefined') {
    return html.replace(/<span[^>]*data-variable="([^"]*)"[^>]*>[\s\S]*?<\/span>/g, '$1');
  }

  const div = document.createElement('div');
  div.innerHTML = html;

  div.querySelectorAll('.ql-variable-chip').forEach(chip => {
    const variable = chip.getAttribute('data-variable') || chip.textContent || '';
    chip.replaceWith(document.createTextNode(variable));
  });

  div.querySelectorAll('span[style]').forEach(span => {
    const style = span.getAttribute('style') || '';
    const cleaned = style
      .replace(/\bcolor\s*:\s*[^;]+;?/gi, '')
      .replace(/\bbackground(-color)?\s*:\s*[^;]+;?/gi, '')
      .replace(/\bfont-family\s*:\s*monospace[^;]*;?/gi, '')
      .trim()
      .replace(/;+\s*$/, '');
    if (cleaned) {
      span.setAttribute('style', cleaned);
    } else {
      span.removeAttribute('style');
    }
  });

  return div.innerHTML;
}
